import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Dialog } from 'primereact/dialog';

import { DocumentoSeleccionado } from '@/types/getPendingPaymentsType';

import { Button } from '@/components';
import { Divider } from 'primereact/divider';

function parseAmount(amount: number) {
  const value = amount.toLocaleString('es-UR', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });

  return `U$S ${value}`;
}

type PendingWithholdingsModalProps = {
  items: DocumentoSeleccionado[];
  onClose: (res?: boolean) => void;
  opened: boolean;
};

const PendingWithholdingsModal: FC<PendingWithholdingsModalProps> = ({
  items = [],
  onClose = () => {},
  opened = false,
}) => {
  const { t } = useTranslation('accountStatement');

  const dialogHeader = (
    <div className="text-blue-800">{t('pendingWithholdings')}</div>
  );

  const amounts = useMemo(() => {
    const body = new Map<string, number>();
    (items || []).forEach((item) => {
      const referefence = item.referenciaLiquidacion || t('withoutReference');
      const currentAmount = body.get(referefence) || 0;
      const newAmount = currentAmount + Number(item.montoRetencion);
      body.set(referefence, newAmount);
    });

    const options = Array.from(body, ([key, value]) => ({
      key,
      value: parseAmount(value),
    }));

    return options;
  }, [items, t]);

  const total = useMemo(() => {
    const amount = (items || []).reduce((acc, item) => {
      return acc + Number(item.montoRetencion);
    }, 0);

    const amountString = parseAmount(amount);

    return amountString;
  }, [items]);

  return (
    <Dialog
      header={dialogHeader}
      visible={opened}
      style={{ width: '480px' }}
      onHide={() => onClose(false)}
      modal
      breakpoints={{ '500px': '95vw' }}
      footer={
        <div className="w-full flex justify-content-end gap-1 pb-2">
          <Button
            className="w-auto px-3"
            label={t('cancel')}
            onClick={() => onClose(false)}
            style={{ color: '#170a33' }}
            text
          />
          <Button className="w-auto px-3" onClick={() => onClose(true)} green>
            {t('accept')}
          </Button>
        </div>
      }
    >
      <div className="w-full p-3 pt-0">
        <div className="w-full flex justify-content-between">
          <b className="text-sm text-gray-900">{t('table.reference')}</b>
          <b className="text-sm text-gray-900">{t('withholding')}</b>
        </div>
        {amounts?.map(({ key, value }) => (
          <div key={key} className="w-full flex justify-content-between">
            <p className="text-sm">{key}</p>
            <small className="text-sm">{value}</small>
          </div>
        ))}
        <Divider className="w-full m-1 mb-2" />
        <div className="w-full flex justify-content-end">
          <div className="flex gap-2">
            <b className="text-sm text-gray-900">{t('total')}</b>
            <small className="text-sm">{total}</small>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default PendingWithholdingsModal;
